/**
 * The renew-decision sub-form — extracted VERBATIM from PeriodModal's create branch (the section title +
 * hint, the 3-option Undecided/Yes/No radiogroup, and the nested "No" box with the expiry-action radios
 * over POOL_EXPIRY_ACTIONS + the grace_2stage grace-days input). Purely controlled: it holds no state of
 * its own, so PeriodModal's create branch AND BulkSetPeriodDialog render the SAME markup/i18n keys from
 * ONE definition instead of each maintaining a copy that would drift.
 *
 * `RenewRadio`/`ExpiryActionRadio`/`ACTION_LABEL_KEY` moved here too (were PeriodModal-private); PeriodModal
 * imports them back for its EDIT branch, which keeps its own inline layout (different renew-now semantics)
 * and is deliberately NOT refactored to use this component.
 *
 * 30/7 (คุณใหม่ bug 1) — picking "Yes" now reveals <NextRoundFields/> right here, so a renewal is one save
 * on every surface that renders this (create form + bulk dialog), and the edit branch renders the SAME
 * sub-component. Before this, "Yes" had nowhere to go on the create form and the server 422'd it.
 */
import { useTranslation } from "react-i18next";
import { DatePicker, Input } from "@/components/ui";
import { dayAfter } from "@/features/pool/dateUtils";
import { graceDaysVisible, type RenewChoice } from "@/features/pool/renewValidation";
import { POOL_EXPIRY_ACTIONS } from "@/api/types";
import type { PoolExpiryAction } from "@/api/types";
import type { TKey } from "@/i18n/keys";

/** A small, statically-known enum → its i18n key. A `Record` literal (not a template-built key) so
 *  every value is compile-checked against the real resource tree, and so `t()` never has to resolve a
 *  dynamic template-literal key. */
export const ACTION_LABEL_KEY = {
  license_only: "pool.period.actionLicenseOnly",
  grace_2stage: "pool.period.actionGrace",
  disassociate_now: "pool.period.actionDisassociate",
} as const satisfies Record<PoolExpiryAction, TKey>;

export interface RenewDecisionFieldsProps {
  renewChoice: RenewChoice;
  onRenewChoice: (v: RenewChoice) => void;
  expiryAction: PoolExpiryAction;
  onExpiryAction: (v: PoolExpiryAction) => void;
  graceDays: string;
  onGraceDays: (v: string) => void;
  /** The current round's end date — the anchor the next round's start is derived from (30/7). */
  periodEnd: string;
  nextEnd: string;
  onNextEnd: (v: string) => void;
}

export function RenewDecisionFields({
  renewChoice,
  onRenewChoice,
  expiryAction,
  onExpiryAction,
  graceDays,
  onGraceDays,
  periodEnd,
  nextEnd,
  onNextEnd,
}: RenewDecisionFieldsProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
          {t("pool.period.renewSectionTitle")}
        </h3>
        <p className="text-(length:--text-2xs) text-(--text-subtle)">
          {t("pool.period.renewSectionHint")}
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label={t("pool.period.renewSectionTitle")}
        className="flex flex-col gap-2"
      >
        <RenewRadio
          value="undecided"
          checked={renewChoice === "undecided"}
          label={t("pool.period.renewUndecidedLabel")}
          onSelect={onRenewChoice}
        />
        <RenewRadio
          value="yes"
          checked={renewChoice === "yes"}
          label={t("pool.period.renewYesLabel")}
          onSelect={onRenewChoice}
        />
        <RenewRadio
          value="no"
          checked={renewChoice === "no"}
          label={t("pool.period.renewNoLabel")}
          onSelect={onRenewChoice}
        />
      </div>

      {renewChoice === "yes" && (
        <NextRoundFields periodEnd={periodEnd} nextEnd={nextEnd} onNextEnd={onNextEnd} />
      )}

      {renewChoice === "no" && (
        <div className="flex flex-col gap-3 rounded-(--radius) border border-(--border) p-3">
          <div
            role="radiogroup"
            aria-label={t("pool.period.expiryActionLabel")}
            className="flex flex-col gap-2"
          >
            <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
              {t("pool.period.expiryActionLabel")}
            </span>
            {POOL_EXPIRY_ACTIONS.map((a) => (
              <ExpiryActionRadio
                key={a}
                action={a}
                checked={expiryAction === a}
                label={t(ACTION_LABEL_KEY[a])}
                onSelect={onExpiryAction}
              />
            ))}
          </div>

          {graceDaysVisible(expiryAction) && (
            <label className="flex flex-col gap-1.5">
              <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                {t("pool.period.graceDaysLabel")}
              </span>
              <Input
                type="number"
                min={1}
                value={graceDays}
                onChange={(e) => onGraceDays(e.target.value)}
                placeholder={t("pool.period.graceDaysHint")}
                className="w-32"
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The next round, revealed the moment "Yes" is picked (30/7, คุณใหม่ bug 1: "กด Yes แล้วจะให้เลือก service
 * Period รอบถัดไปเลย" — not save → reopen → Yes → dates).
 *
 * The START is shown, never edited: it is always the day after the current round's last day ("ห้ามเว้น —
 * ต่อกันทันที"), computed with the SAME rule the server stores (dateUtils.dayAfter ↔ periods.next_period_
 * start_for ↔ the `next_period_contiguous` CHECK). Rendering it read-only is the point — an editable
 * start would invite a gap the constraint then rejects, and a hidden one would make the operator guess
 * what they just agreed to. Only the end date is theirs.
 *
 * Not a DateRangePicker: a range implies both ends are in play — one date, one decision. Until 31/7 this
 * field used the browser's native `<input type="date">` for exactly that reason (building a whole second
 * masked/calendar widget just to pick ONE date wasn't worth it for one field) — but that native input was
 * the ONLY one left in the app, carrying the same OS-calendar landmine DateRangePicker exists to dodge
 * (Thai Buddhist-era dates popping up over ours). Extracting DateRangePicker's masked-input/popover/grid
 * internals into dateFieldShared.tsx made a real single-date DatePicker cheap enough to justify — see
 * components/ui/DatePicker.tsx — so this now uses that instead.
 */
export function NextRoundFields({
  periodEnd,
  nextEnd,
  onNextEnd,
}: {
  periodEnd: string;
  nextEnd: string;
  onNextEnd: (v: string) => void;
}) {
  const { t } = useTranslation();
  const nextStart = dayAfter(periodEnd);
  return (
    <div className="flex flex-col gap-2 rounded-(--radius) border border-(--border) p-3">
      <p className="text-(length:--text-xs) font-medium text-(--text-muted)">
        {t("pool.period.nextRoundTitle")}
      </p>
      <p className="text-(length:--text-2xs) text-(--text-subtle)">
        {t("pool.period.nextRoundHint", { date: nextStart })}
      </p>
      <label className="flex flex-col gap-1.5">
        <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
          {t("pool.period.nextEndLabel")}
        </span>
        <DatePicker
          min={nextStart}
          value={nextEnd}
          onChange={onNextEnd}
          aria-label={t("pool.period.nextEndLabel")}
          className="w-44"
        />
      </label>
    </div>
  );
}

export function ExpiryActionRadio({
  action,
  checked,
  label,
  onSelect,
}: {
  action: PoolExpiryAction;
  checked: boolean;
  label: string;
  onSelect: (v: PoolExpiryAction) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-(length:--text-sm) text-(--text)">
      <input
        type="radio"
        name="pool-expiry-action"
        checked={checked}
        onChange={() => onSelect(action)}
        className="size-4 accent-(--brand)"
      />
      {label}
    </label>
  );
}

export function RenewRadio({
  value,
  checked,
  label,
  onSelect,
}: {
  value: RenewChoice;
  checked: boolean;
  label: string;
  onSelect: (v: RenewChoice) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-(length:--text-sm) text-(--text)">
      <input
        type="radio"
        name="pool-renew-choice"
        checked={checked}
        onChange={() => onSelect(value)}
        className="size-4 accent-(--brand)"
      />
      {label}
    </label>
  );
}
